import {
  AssistantRuntimeProvider,
  ComposerPrimitive,
  MessagePrimitive,
  ThreadPrimitive,
  useAuiState,
} from '@assistant-ui/react';
import { Box, Flex } from '@chakra-ui/react';
import { Colors, Text } from '@mercator-gcp/grout';
import { ReactComponent as ScountAgentLogo } from 'components/assets/scount-agent-logo.svg';
import StreamingText from 'components/elements/StreamingText';
import { FlexRow } from 'components/layout/FlexRow';
import SavedSearchChangeCard from 'components/widgets/Chat/SavedSearchChangeCard';
import FeedbackControl from 'components/widgets/ScoutAgent/MainView/FeedbackControl';
import LoadingBubble from 'components/widgets/ScoutAgent/MainView/LoadingBubble';
import SuggestCorrectionFooter from 'components/widgets/SuggestCorrection/Footer';
import { useScoutChatRuntime } from 'hooks/useScoutChatRuntime';
import type { SavedSearchChange, Session } from 'services/API/AIInteractionAPI';

const chatContainerStyle = {
  flex: 1,
  minHeight: 0,
  flexDirection: 'column',
  maxWidth: '960px',
  margin: '0 auto',
  height: '100%',
  paddingBottom: 'padding_16',
};

const scrollContainerStyle = {
  flex: 1,
  minHeight: 0,
  padding: 'padding_16',
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 'padding_12',
  overflow: 'auto',
  paddingTop: 'padding_48',
};

const logoStyle = {
  alignSelf: 'center',
  marginBottom: 'padding_8',
};

const disclaimerStyle = {
  color: Colors.TextQuiet,
  textAlign: 'center',
  marginBottom: 'padding_24',
};

const inputWrapperStyle = {
  padding: 'padding_16',
  flexShrink: 0,
};

const footerStyle = {
  color: Colors.TextQuiet,
  justifyContent: 'space-between',
  paddingX: 'padding_24',
};

const bubbleBaseStyle = {
  maxWidth: '85%',
  paddingY: 'padding_12',
  paddingX: 'padding_16',
  borderRadius: '12px',
  wordBreak: 'break-word' as const,
};

const agentBubbleStyle = {
  ...bubbleBaseStyle,
  alignSelf: 'flex-start',
  backgroundColor: Colors.ArmyGreen100,
  borderBottomLeftRadius: '4px',
};

const userBubbleStyle = {
  ...bubbleBaseStyle,
  alignSelf: 'flex-end',
  backgroundColor: Colors.ForestGreen200,
  color: Colors.Black900,
  borderBottomRightRadius: '4px',
};

const agentBubbleWrapperStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'flex-start',
  alignSelf: 'flex-start',
  gap: 'padding_4',
};

const composerWrapperStyle = {
  position: 'relative' as const,
  borderWidth: '2px',
  borderStyle: 'solid',
  borderColor: 'gray.200',
  borderRadius: '24px',
  backgroundColor: Colors.ArmyGreen200,
  width: '100%',
};

const COMPOSER_MIN_ROWS = 1;
const COMPOSER_MAX_ROWS = 12;

const composerInputStyle = {
  width: '100%',
  padding: '12px 48px 12px 24px',
  border: 'none',
  outline: 'none',
  boxShadow: 'none',
  borderRadius: '24px',
  backgroundColor: 'transparent',
  resize: 'none' as const,
  font: 'inherit',
  color: 'inherit',
  boxSizing: 'border-box' as const,
};

const composerSendWrapperStyle = {
  position: 'absolute',
  right: '8px',
  top: '6px',
};

const composerSendButtonStyle = {
  borderRadius: '16px',
  minWidth: '32px',
  height: '32px',
  padding: 0,
};

type ChatViewProps = {
  chat: Session;
  showFooter?: boolean;
  inputPlaceholder?: string;
  onSavedSearchChange?: (changes: SavedSearchChange[]) => void;
  isAwaitingResponse?: boolean;
};

/**
 * Renders one text part. Empty text + a "running" status is the synthetic
 * placeholder assistant-ui synthesizes while a message has no parts yet -
 * see useScoutChatRuntime's runningMessage.
 */
const ScoutMessageText = ({
  text,
  status,
}: {
  text: string;
  status?: { type: string };
}) => {
  if (status?.type === 'running' && text === '') {
    return <LoadingBubble />;
  }

  return <StreamingText text={text} speed={0} />;
};

const ScoutFeedback = ({ sessionId }: { sessionId: string }) => {
  const messageId = useAuiState((s) => s.message.id);

  return <FeedbackControl sessionId={sessionId} eventTimestamp={messageId} />;
};

const ScoutUserMessage = () => (
  <Box sx={userBubbleStyle}>
    <MessagePrimitive.Root>
      <MessagePrimitive.Parts components={{ Text: ScoutMessageText }} />
    </MessagePrimitive.Root>
  </Box>
);

const ScoutAssistantMessage = ({ sessionId }: { sessionId: string }) => (
  <Box sx={agentBubbleWrapperStyle}>
    <Box sx={agentBubbleStyle}>
      <MessagePrimitive.Root>
        <MessagePrimitive.Parts
          components={{
            Text: ScoutMessageText,
            data: { by_name: { 'saved-search-change': SavedSearchChangeCard } },
          }}
        />
      </MessagePrimitive.Root>
    </Box>
    <ScoutFeedback sessionId={sessionId} />
  </Box>
);

const ChatView = ({
  chat,
  showFooter = true,
  inputPlaceholder,
  onSavedSearchChange,
  isAwaitingResponse,
}: ChatViewProps) => {
  const runtime = useScoutChatRuntime(chat, onSavedSearchChange, isAwaitingResponse);

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <ThreadPrimitive.Root asChild>
        <Flex sx={chatContainerStyle}>
          <ThreadPrimitive.Viewport asChild>
            <Box sx={scrollContainerStyle}>
              <Box sx={logoStyle}>
                <ScountAgentLogo />
              </Box>
              <Box sx={disclaimerStyle}>
                <Text as="p">
                  You’ve got exclusive early access to Scout — built for quick
                  exploration.
                </Text>
                <Text as="p">
                  It’s still evolving, and your feedback drives improvements.
                </Text>
              </Box>
              <ThreadPrimitive.Messages>
                {({ message }) =>
                  message.role === 'user' ? (
                    <ScoutUserMessage />
                  ) : (
                    <ScoutAssistantMessage sessionId={chat.session_id} />
                  )
                }
              </ThreadPrimitive.Messages>
            </Box>
          </ThreadPrimitive.Viewport>
          <Box sx={inputWrapperStyle}>
            <ComposerPrimitive.Root asChild>
              <Box as="form" sx={composerWrapperStyle}>
                <ComposerPrimitive.Input
                  placeholder={inputPlaceholder}
                  style={composerInputStyle}
                  minRows={COMPOSER_MIN_ROWS}
                  maxRows={COMPOSER_MAX_ROWS}
                />
                <Box sx={composerSendWrapperStyle}>
                  <ComposerPrimitive.Send asChild>
                    <Box
                      as="button"
                      type="submit"
                      aria-label="Submit"
                      sx={{
                        ...composerSendButtonStyle,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: Colors.ForestGreen900,
                        color: Colors.White,
                        border: 'none',
                        cursor: 'pointer',
                        '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
                      }}
                    >
                      →
                    </Box>
                  </ComposerPrimitive.Send>
                </Box>
              </Box>
            </ComposerPrimitive.Root>
          </Box>
          {showFooter && (
            <FlexRow sx={footerStyle}>
              <Text>Scout is still learning, and your feedback helps shape it.</Text>
              <SuggestCorrectionFooter variant="inline" />
            </FlexRow>
          )}
        </Flex>
      </ThreadPrimitive.Root>
    </AssistantRuntimeProvider>
  );
};

export default ChatView;
